import WarningIcon from '@jetbrains/icons/warning.svg'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import plur from 'plur'
import {useMemo, memo} from 'react'

import {useAppSelector} from '../../../../../hooks/react-redux'
import {emptyArray} from '../../../../../utils/empty'
import RepositoryTag from '../../components/Badges/RepositoryBadge/RepositoryTag/RepositoryTag'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs'
import {getEditPipelineBreadcrunbs} from '../../components/Breadcrumbs/Breadcrumbs.utils'
import ButtonLink from '../../components/ButtonLink/ButtonLink'
import Description from '../../components/Description/Description'
import type {pipelineHeadFragment$key} from '../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../hooks/pipeline'
import usePipelineDocumentTitle from '../../hooks/usePipelineDocumentTitle'
import {isStoreYamlInVcsEnabled} from '../../utils/featureToggles'
import {useJobId, usePipelineNameOrDraft} from '../EditPipelinePage.hooks'
import {
  getDraftPipelineAdditionalVcsRoots,
  makeTotalCountPipelineFormErrorsSelector,
} from '../EditPipelinePage.selectors'
import SaveChanges from '../SaveChanges/SaveChanges'
import {getRepositoryName} from '../SettingsSidebarContent/PipelineSettingsSidebar/PipelineRepositories/PipelineRepositories.utils'

import styles from './EditPipelineHeader.css'

type Props = {
  className?: string
  pipelineHeadKey: pipelineHeadFragment$key | null
}

function EditPipelineHeader({className, pipelineHeadKey}: Props) {
  const selectedJob = useJobId()
  const id = usePipelineId()
  const name = usePipelineNameOrDraft()
  const isCollisionApi = useAppSelector(state => state.pipelines.pipelineDraft[id]?.isCollisionApi)
  const vcsRootUrl = useAppSelector(state =>
    getRepositoryName(state.pipelines.pipelineDraft[id]?.draft?.vcsRoot.url),
  )
  const selector = useMemo(() => makeTotalCountPipelineFormErrorsSelector(), [])
  const pipelineAdditionalVcsRoots =
    useAppSelector(getDraftPipelineAdditionalVcsRoots(id)) ?? emptyArray
  const deletedRepositories = useAppSelector(
    state => state.pipelines.pipelineDraft[id]?.deleted?.additionalVcsRoots,
  )
  const deletedRepositoriesSet = useMemo(() => new Set(deletedRepositories), [deletedRepositories])
  const additionalVcsRoots = useMemo(
    () =>
      pipelineAdditionalVcsRoots.reduce<string[]>((acc, {url}) => {
        const isDeleted = deletedRepositoriesSet.has(url)
        const path = url.replace(/^https?:\/\//, '') ?? ''

        if (!isDeleted && path) {
          acc.push(path)
        }

        return acc
      }, []),
    [deletedRepositoriesSet, pipelineAdditionalVcsRoots],
  )
  const pipelineNumberErrors = useAppSelector(state =>
    selector(state, {
      pipelineId: id,
    }),
  )
  const errorLabel = pipelineNumberErrors
    ? `${pipelineNumberErrors} ${plur('error', pipelineNumberErrors)}`
    : ''

  usePipelineDocumentTitle(`Edit ${name}`)

  return (
    <div className={classNames(className, styles.header)}>
      <div className={styles.breadcrumbs}>
        <Breadcrumbs path={getEditPipelineBreadcrunbs(id, name)} />
      </div>
      <div className={styles.heading}>
        <div className={styles.titleWrapper}>
          <H1 className={styles.title} title={name}>
            {name}
          </H1>
          {errorLabel && <div className={styles.numberErrors}>{errorLabel}</div>}
        </div>
        {vcsRootUrl != null && (
          <div className={styles.vcsRoots}>
            <Description className={styles.description} text={vcsRootUrl} />
            {!!additionalVcsRoots?.length && (
              <RepositoryTag
                className={styles.additionalVcsRoots}
                repositories={additionalVcsRoots}
              />
            )}
          </div>
        )}
        {!isStoreYamlInVcsEnabled && isCollisionApi && (
          <div className={styles.warningMessage}>
            <WarningIcon />
            {'This Pipeline was modified, reload the page to view updated settings'}
          </div>
        )}
      </div>
      <div className={styles.actions}>
        {selectedJob && (
          <ButtonLink
            to={{search: ''}}
            className={classNames(styles.headerLink, {
              [styles.active]: !selectedJob,
            })}
          >
            {'Settings'}
          </ButtonLink>
        )}
        <SaveChanges pipelineHeadKey={pipelineHeadKey} />
      </div>
    </div>
  )
}

export default memo(EditPipelineHeader)
